import { memo, useCallback } from "react";
import { Controller } from "react-hook-form";

import FormItem    from "../../components/ui/FormItem";
import FormGrid    from "../../components/ui/FormGrid";
import FormActions from "../ui/FormActions";
import Dropdown    from "../common/dropdown/Dropdown";
import FileUpload  from "../common/fileupload/FileUpload";
import Calendar    from "../common/calendar/Calendar";
import Button      from "../ui/Button";
import BudgetInput from "./BudgetInput";
import ExtractDataModal from "./ExtractDataModal";
import Loader      from "../ui/Loader/Loader";

import { usePropertyStore } from "../../store/propertyStore";
import { PROPERTY_TYPES, UNIT_TYPES } from "../../data/intialData";
import { formatDateDDMMYYYY, parseDateString } from "../../hooks/commonHooks";
import { usePropertyForm } from "./usePropertyForm";

const ALL_UNIT_TYPES_OPTION = "all";
const UNIT_TYPE_OPTIONS = [ALL_UNIT_TYPES_OPTION, ...UNIT_TYPES];


const MemoizedExtractDataModal = memo(ExtractDataModal);

export default function PropertyForm({
  initialData = {},
  onSubmit,
  handleExtractPdfData,
  handleExtractFloorPlanData,
  isLoading,
  isPDFExtracting,
  isFloorPlanExtracting,
  setIsBrochureExtractDataOpen,
  setIsFloorPlanExtractDataOpen,
  onCancel,
  isBrochureExtractDataOpen,
  isFloorPlanExtractDataOpen,
  onCloseBrochureExtractData,
  onCloseFloorPlanExtractData,
}) {
  const extractedPdfData = usePropertyStore((s) => s.extractedPdfData);
  const extractedFloorPlanData = usePropertyStore((s) => s.extractedFloorPlanData);


  const {
    register,
    STATIC_RULES,
    control,
    errors,
    brochure,
    floorPlan,
    expectedDelivery,
    propertyType,
    showCalendar,
    setShowCalendar,
    onFormSubmit,
    isValidated,
    handleCancel,
    isEditMode,
    minBudgetUnitType,
    setMinBudgetUnitType,
    maxBudgetUnitType,
    setMaxBudgetUnitType,
  } = usePropertyForm(initialData, onSubmit, onCancel);

  // ✅ stable callbacks — deps are either stable functions or primitive values
  const handleOpenCalendar  = useCallback(() => setShowCalendar(true), [setShowCalendar]);
  const handleExtractClick  = useCallback(() => handleExtractPdfData(brochure), [handleExtractPdfData, brochure]);
  const handleViewExtracted = useCallback(() => setIsBrochureExtractDataOpen(true), [setIsBrochureExtractDataOpen]);

  const handleExtractFloorPlanClick = useCallback(
    () => handleExtractFloorPlanData(floorPlan),
    [handleExtractFloorPlanData, floorPlan]
  );
  const handleViewExtractedFloorPlan = useCallback(
    () => setIsFloorPlanExtractDataOpen(true),
    [setIsFloorPlanExtractDataOpen]
  );

  return (
    <>
      <section className="property-form position-relative">
        <form onSubmit={onFormSubmit}>
          <FormGrid>

            {/* ── Native inputs: register with STATIC_RULES (stable object ref) ── */}
            <FormItem label="Property Name" error={errors.name?.message} required>
              <input className="form-input" {...register("name", STATIC_RULES.name)} />
            </FormItem>

            <FormItem label="City" error={errors.city?.message} required>
              <input className="form-input" {...register("city", STATIC_RULES.city)} />
            </FormItem>

            <FormItem label="Area" error={errors.area?.message} required>
              <input className="form-input" {...register("area", STATIC_RULES.area)} />
            </FormItem>

            <FormItem label="State" error={errors.state?.message} required>
              <input className="form-input" {...register("state", STATIC_RULES.state)} />
            </FormItem>

            <FormItem label="Country" error={errors.country?.message} required>
              <input className="form-input" {...register("country", STATIC_RULES.country)} />
            </FormItem>

            <FormItem label="Pincode" error={errors.pincode?.message} required>
              <input className="form-input" maxLength={6} {...register("pincode", STATIC_RULES.pincode)} />
            </FormItem>

            <FormItem label="Site Property URL" error={errors.site_property_url?.message}>
              <input className="form-input" {...register("site_property_url", STATIC_RULES.site_property_url)} />
            </FormItem>

            {/* ── Custom components: Controller ── */}
            <FormItem label="Property Type" error={errors.property_type?.message} required>
              <Controller
                name="property_type"
                control={control}
                rules={STATIC_RULES.property_type}
                render={({ field }) => (
                  <Dropdown options={PROPERTY_TYPES} value={field.value} onChange={field.onChange} />
                )}
              />
            </FormItem>

            <FormItem label="Development Status" required>
              <Controller
                name="possession_status"
                control={control}
                rules={STATIC_RULES.possession_status}
                render={({ field }) => (
                  <Dropdown
                    options={["ongoing", "completed", "upcoming"]}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormItem>

            <FormItem label="Unit Type" error={errors.unit_type?.message} required>
              <Controller
                name="unit_type"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    options={UNIT_TYPE_OPTIONS}
                    multiple
                    disabled={propertyType === "plot"}
                    value={field.value}
                    onChange={(value) =>
                      field.onChange(
                        value.includes(ALL_UNIT_TYPES_OPTION)
                          ? UNIT_TYPES
                          : value.filter((u) => u !== ALL_UNIT_TYPES_OPTION)
                      )
                    }
                    shouldCloseOnSelect={(opt) => opt === ALL_UNIT_TYPES_OPTION}
                  />
                )}
              />
            </FormItem>

            <Controller
              name="min_budget"
              control={control}
              rules={STATIC_RULES.min_budget}
              render={({ field }) => (
                <BudgetInput
                  label="Min Budget"
                  value={field.value}
                  unit={minBudgetUnitType}
                  onValueChange={field.onChange}
                  onUnitChange={setMinBudgetUnitType}
                  error={errors.min_budget?.message}
                  required
                />
              )}
            />

            <Controller
              name="max_budget"
              control={control}
              rules={STATIC_RULES.max_budget}
              render={({ field }) => (
                <BudgetInput
                  label="Max Budget"
                  value={field.value}
                  unit={maxBudgetUnitType}
                  onValueChange={field.onChange}
                  onUnitChange={setMaxBudgetUnitType}
                  error={errors.max_budget?.message}
                  required
                />
              )}
            />

            <FormItem
              label="Expected Delivery"
              className="position-relative"
              error={errors.expected_delivery?.message}
              required
            >
              <div className="form-input text-left" onClick={handleOpenCalendar}>
                {expectedDelivery ? formatDateDDMMYYYY(expectedDelivery) : "Select date"}
              </div>
              {showCalendar && (
                <div className="calendar-popover">
                  <Controller
                    name="expected_delivery"
                    control={control}
                    rules={STATIC_RULES.expected_delivery}
                    render={({ field }) => (
                      <Calendar
                        value={parseDateString(field.value) ?? undefined}
                        onChange={(date) => { field.onChange(date); setShowCalendar(false); }}
                      />
                    )}
                  />
                </div>
              )}
            </FormItem>

            <FormItem label="Image" error={errors.image?.message} required>
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <FileUpload
                    name="image"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.files[0])}
                  />
                )}
              />
            </FormItem>

          </FormGrid>

          <div className="d-flex flex-column gap-3">
            {/* Brochure (PDF) */}
            <div className="upload-row">
              <FormItem label="Brochure (PDF)" error={errors.brochure?.message}>
                <Controller
                  name="brochure"
                  control={control}
                  render={({ field }) => (
                    <FileUpload name="brochure" value={field.value} onChange={(e) => field.onChange(e.target.files[0])} />
                  )}
                />
              </FormItem>

              <div className="upload-row-actions">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={handleExtractClick}
                  disabled={!brochure || isLoading}
                >
                  Extract PDF Data
                </Button>

                {extractedPdfData && (
                  <Button type="button" variant="secondary" size="md" onClick={handleViewExtracted}>
                    View Extracted Data
                  </Button>
                )}
              </div>
            </div>

            {/* Floor Plan (optional) */}
            <div className="upload-row">
              <FormItem label="Floor Plan (optional)" error={errors.floor_plan?.message}>
                <Controller
                  name="floor_plan"
                  control={control}
                  render={({ field }) => (
                    <FileUpload name="floor_plan" value={field.value} onChange={(e) => field.onChange(e.target.files[0])} />
                  )}
                />
              </FormItem>

              <div className="upload-row-actions">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={handleExtractFloorPlanClick}
                  disabled={!floorPlan || isLoading}
                >
                  Extract Floor Plan Data
                </Button>

                {extractedFloorPlanData && (
                  <Button type="button" variant="secondary" size="md" onClick={handleViewExtractedFloorPlan}>
                    View Extracted Data
                  </Button>
                )}
              </div>
            </div>
          </div>

          <FormActions
            isLoading={isLoading}
            disabled={!isValidated}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
            saveText={isEditMode ? "Update Property" : "Create Property"}
            onCancel={handleCancel}
          />
        </form>
      </section>

      {(isPDFExtracting || isFloorPlanExtracting) && <Loader />}

      {isBrochureExtractDataOpen && (
        <MemoizedExtractDataModal
          details={extractedPdfData}
          isOpen={isBrochureExtractDataOpen}
          onClose={onCloseBrochureExtractData}
          title="Extracted Brochure Data"
        />
      )}

      {isFloorPlanExtractDataOpen && (
        <MemoizedExtractDataModal
          details={extractedFloorPlanData}
          isOpen={isFloorPlanExtractDataOpen}
          onClose={onCloseFloorPlanExtractData}
          title="Extracted Floor Plan Data"
        />
      )}
    </>
  );
}
